import { useTranslation } from "react-i18next";
import "./application_tracking_widget.scss";
import { useEffect, useState } from "react";
import { useEffectOnce } from "react-use";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import { Button } from "./button";
import { Body } from "./typography";
import { SnowplowEvent, useSnowplowTracker } from "~/hooks/use_snowplow_tracker";
import serverAssetUrl from "~/utils/asset_url.server";
import { serverOnly$ } from "vite-env-only/macros";

const HEADER_VARIANTS = ["header_a", "header_b", "header_c", "header_d"] as const;
type HeaderVariant = (typeof HEADER_VARIANTS)[number];
const DEFAULT_HEADER_VARIANT: HeaderVariant = "header_a";

const randomHeaderVariant = (): HeaderVariant =>
  HEADER_VARIANTS[Math.floor(Math.random() * HEADER_VARIANTS.length)];

const variantEventData = (trackedVariant?: HeaderVariant) =>
  trackedVariant ? { variant: trackedVariant } : {};

const variantUrlParam = (trackedVariant?: HeaderVariant) =>
  trackedVariant ? `&variant=${trackedVariant}` : "";

const ApplicationTrackingWidget = () => {
  const { t } = useTranslation("job_post");
  const [jobSeekersUrl, setJobSeekersUrl] = useState("");
  const [headerVariant, setHeaderVariant] = useState<HeaderVariant>();
  const { preview_mode, button_shape } = useBoardConfiguration();
  const { trackEvent } = useSnowplowTracker();
  const assetUrl = serverOnly$(serverAssetUrl) || window.ENV.ASSET_URL;

  const useVariants = !!headerVariant && window.ENV.TRACK_STATUS_HEADER_VARIANTS;

  useEffectOnce(() => {
    setHeaderVariant(
      window.ENV.TRACK_STATUS_HEADER_VARIANTS ? randomHeaderVariant() : DEFAULT_HEADER_VARIANT
    );
  });

  useEffect(() => {
    headerVariant &&
      !preview_mode &&
      trackEvent(SnowplowEvent.MyGreenhouseAdShown, {
        type: "track_your_status",
        ...variantEventData(useVariants ? headerVariant : undefined),
      });
  });

  useEffect(() => {
    setJobSeekersUrl(
      `${window.ENV.JOB_SEEKERS_URL}/users/sign_in?source=track_your_status${variantUrlParam(
        useVariants ? headerVariant : undefined
      )}`
    );
  }, [headerVariant, useVariants]);

  const trackClick = () => {
    !preview_mode &&
      trackEvent(SnowplowEvent.MyGreenhouseAdClicked, {
        type: "track_your_status",
        ...variantEventData(useVariants ? headerVariant : undefined),
      });
  };

  return (
    <div className="application_tracking_widget">
      <div className="application_tracking_widget__icon">
        <img src={`${assetUrl}/greenhouse_white_g.svg`} alt="MyGreenhouse Application Tracking" />
      </div>
      <div className="application_tracking_widget__content">
        <div className="application_tracking_widget__content__header">
          <Body medium>{headerVariant && t(`application_tracking_widget.${headerVariant}`)}</Body>
          <Body>{t("application_tracking_widget.body")}</Body>
        </div>
        <Button
          secondary
          link
          href={preview_mode ? undefined : jobSeekersUrl}
          target="_blank"
          shape={button_shape}
          onClick={trackClick}
        >
          {t("application_tracking_widget.create_account")}
        </Button>
      </div>
    </div>
  );
};

export default ApplicationTrackingWidget;
